import _defineProperty from "@babel/runtime/helpers/defineProperty";
import React, { useContext } from 'react';
import clsx from 'clsx';
import { fade } from '@material-ui/core/styles/colorManipulator';
import { makeStyles } from '@material-ui/core/styles'; // Import Components

import { Advertisement } from '../Advertisement';
import { Breadcrumbs } from '../Breadcrumbs';
import { ContentHeroBannerSubHeader } from '../ContentHeroBanner';
import { ContentSocialBlock } from '../ContentSocialBlock';
import { makeImgResizerUrl } from '@jelly/utils';
import { SiteConfigContext } from '../SiteConfigProvider';
import { SponsoredLink } from '../SponsoredLink';
import Typography from '@material-ui/core/Typography';
import { GradientContainer } from '@jelly/ui';
var useStyles = makeStyles(function (theme) {
  var _SubTitle, _Title;

  return {
    BreadcrumbsWrapper: _defineProperty({
      fontSize: '12px',
      margin: 'auto',
      width: '90%'
    }, theme.breakpoints.up('sm'), {
      background: 'none',
      fontSize: '16px',
      margin: 'auto 0 auto 23px',
      right: '0',
      width: '98%'
    }),
    HeaderContext: {
      minHeight: '200px',
      flexDirection: 'column',
      width: '100%',
      '&.showOverlappingContent': {
        paddingBottom: function paddingBottom(_ref) {
          var isPartOfVerticalStackTest = _ref.isPartOfVerticalStackTest;
          return isPartOfVerticalStackTest ? '0px' : '140px';
        }
      }
    },
    HeaderWrapper: {
      width: '100%'
    },
    LinkBar: _defineProperty({
      alignItems: 'center',
      display: 'flex',
      flexDirection: 'column',
      height: 'auto',
      width: '100%'
    }, theme.breakpoints.up('sm'), {
      flexDirection: 'row',
      height: '50px',
      background: theme.palette.grey[50]
    }),
    LinksWrapper: _defineProperty({
      background: theme.palette.grey[50],
      display: 'flex',
      height: '40px',
      width: '100%'
    }, theme.breakpoints.up('sm'), {
      background: 'none',
      height: 'auto'
    }),
    PresentedBy: {
      alignItems: 'center',
      display: 'flex',
      flexWrap: 'wrap',
      justifyContent: 'center',
      padding: '0.5rem',
      '& svg': {
        margin: '.5rem'
      }
    },
    SocialWrapper: _defineProperty({
      display: 'none'
    }, theme.breakpoints.up('sm'), {
      display: 'block',
      margin: 'auto 23px auto 0',
      maxWidth: '150px',
      width: '100%',
      div: {
        justifyContent: 'space-evenly'
      }
    }),
    SponsoredContent: {
      color: theme.palette.primary.main,
      margin: '30px auto',
      maxWidth: theme.breakpoints.values.xl,
      textAlign: 'center',
      width: '100%'
    },
    SponsorContext: _defineProperty({
      alignItems: 'center',
      display: 'flex',
      flexDirection: 'column',
      fontSize: '21px',
      justifyContent: 'center',
      margin: '0 auto 16px auto',
      width: '90%',
      svg: {
        margin: '8px'
      }
    }, theme.breakpoints.up('md'), {
      flexDirection: 'row'
    }),
    SponsorLogo: {
      display: 'block',
      height: '50px',
      margin: 'auto 0.5rem',
      width: 'auto'
    },
    SubTitle: (_SubTitle = {
      borderTop: function borderTop(_ref2) {
        var subTitle = _ref2.subTitle;
        return !!subTitle && "1px solid ".concat(fade(theme.palette.common.white, 0.6));
      },
      paddingBottom: function paddingBottom(_ref3) {
        var subTitle = _ref3.subTitle;
        return subTitle && '20px';
      },
      color: function color(_ref4) {
        var isHubTemplate = _ref4.isHubTemplate,
            subTitle = _ref4.subTitle;
        return isHubTemplate && subTitle ? theme.palette.common.white : fade(theme.palette.common.white, 0.6);
      },
      fontSize: '16px',
      fontWeight: 700,
      margin: 'auto',
      maxWidth: '90%',
      paddingTop: '10px',
      textAlign: 'center'
    }, _defineProperty(_SubTitle, theme.breakpoints.up('sm'), {
      fontSize: '18px'
    }), _defineProperty(_SubTitle, theme.breakpoints.up('md'), {
      fontSize: '20px',
      borderTop: 'none'
    }), _defineProperty(_SubTitle, theme.breakpoints.up('xl'), {
      maxWidth: theme.breakpoints.values.xl
    }), _SubTitle),
    StreamText: _defineProperty({
      color: '#fbfbfb',
      display: 'block',
      fontSize: '16px',
      maxWidth: theme.breakpoints.values.xl,
      textAlign: 'center',
      visibility: 'visible',
      margin: function margin(_ref5) {
        var isPartOfVerticalStackTest = _ref5.isPartOfVerticalStackTest;
        return isPartOfVerticalStackTest ? '16px auto' : '16px auto 0 auto';
      },
      width: '90%'
    }, theme.breakpoints.up('md'), {
      display: 'block',
      visibility: 'visible'
    }),
    Title: (_Title = {
      color: theme.palette.common.white,
      fontWeight: 700,
      margin: '0 auto',
      maxWidth: theme.breakpoints.values.xl,
      padding: '30px 0 15px',
      textAlign: 'center',
      width: '90%',
      '&.mbp': {
        color: theme.palette.common.white
      }
    }, _defineProperty(_Title, theme.breakpoints.up('md'), {
      paddingBottom: 0
    }), _defineProperty(_Title, '@media (min-width: 1024px)', {
      width: '100%'
    }), _Title)
  };
}, {
  name: 'simpleHeader'
});

/**
 * This is the Hub Header with the breadcrumb, page title, page subtitle and social sharing buttons
 * Includes breadcrumb schema with the breadcrumb component
 *
 * @param {Object} props.author :: data for the author
 * @param {Object} props.breadcrumbs :: data for the breadcrumbs component (primary and optionary sub)
 * @param {String} props.canonical :: current url path for page
 * @param {String} props.date :: date to display in header
 * @param {Boolean} props.isHubTemplate :: Flag to determine if it is a Hub template of not
 * @param {Object} props.sponsor :: data supplied based on sponsor of the hub
 * @param {Boolean} props.shouldShowLeaderboardAds :: Flag to determine to show leaderboard ads or not
 * @param {Boolean} props.showOverlappingContent :: boolean value to determine if we should add extra height to the banner
 * @param {String} props.streamText :: data to show in header about the page
 * @param {String} props.subTitle :: subtitle of the page
 * @param {String} props.title :: title of the page
 */
export function SimpleHeader(props) {
  var author = props.author,
      breadcrumbs = props.breadcrumbs,
      canonical = props.canonical,
      date = props.date,
      isHubTemplate = props.isHubTemplate,
      published = props.published,
      sponsor = props.sponsor,
      _props$shouldShowLead = props.shouldShowLeaderboardAds,
      shouldShowLeaderboardAds = _props$shouldShowLead === void 0 ? false : _props$shouldShowLead,
      _props$showOverlappin = props.showOverlappingContent,
      showOverlappingContent = _props$showOverlappin === void 0 ? false : _props$showOverlappin,
      streamText = props.streamText,
      _props$subTitle = props.subTitle,
      subTitle = _props$subTitle === void 0 ? null : _props$subTitle,
      title = props.title;
  var isSponsored = !!sponsor;
  var sponsoredLogoSrc = isSponsored && makeImgResizerUrl({
    src: sponsor.logo,
    method: 'h',
    h: '50-png'
  });

  var _useContext = useContext(SiteConfigContext),
      _useContext$siteCompo = _useContext.siteComponents.HUB_PRESENTED_BY_LOGO,
      SiteLogo = _useContext$siteCompo.Svg,
      color = _useContext$siteCompo.color,
      height = _useContext$siteCompo.height,
      width = _useContext$siteCompo.width;

  var classes = useStyles(props);
  return /*#__PURE__*/React.createElement("div", {
    className: classes.HeaderWrapper
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.LinkBar
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.LinksWrapper
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.BreadcrumbsWrapper
  }, /*#__PURE__*/React.createElement(Breadcrumbs, {
    breadcrumbs: breadcrumbs
  }))), /*#__PURE__*/React.createElement("div", {
    className: classes.SocialWrapper
  }, /*#__PURE__*/React.createElement(ContentSocialBlock, {
    canonical: canonical,
    title: title
  }))), shouldShowLeaderboardAds && !isHubTemplate && /*#__PURE__*/React.createElement(Advertisement, {
    isHubTemplate: isHubTemplate
  }), /*#__PURE__*/React.createElement(GradientContainer, {
    className: !isHubTemplate && "mbp"
  }, /*#__PURE__*/React.createElement("div", {
    className: clsx(classes.HeaderContext, {
      showOverlappingContent: showOverlappingContent
    })
  }, /*#__PURE__*/React.createElement(Typography, {
    className: clsx(classes.Title, {
      mbp: !isHubTemplate
    }),
    variant: "h1"
  }, title), (subTitle || author) && /*#__PURE__*/React.createElement(Typography, {
    className: clsx(classes.SubTitle, {
      mbp: !isHubTemplate
    }),
    variant: "h2"
  }, subTitle || /*#__PURE__*/React.createElement(ContentHeroBannerSubHeader, {
    authorType: "staff" // not using the BE value do to too many author types
    ,
    author: author,
    date: date,
    published: published
  })), streamText && /*#__PURE__*/React.createElement("div", {
    className: classes.StreamText
  }, streamText))), isSponsored && /*#__PURE__*/React.createElement("div", {
    className: classes.SponsoredContent
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.SponsorContext
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.PresentedBy
  }, "Presented by ", /*#__PURE__*/React.createElement(SiteLogo, {
    color: color,
    height: height,
    width: width
  }), "in Partnership with"), /*#__PURE__*/React.createElement(SponsoredLink, {
    sponsorName: sponsor.name
  }, /*#__PURE__*/React.createElement("img", {
    className: clsx(classes.SponsoLogo, 'lazyload'),
    alt: sponsor.name,
    "data-src": sponsoredLogoSrc
  })))));
}